import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import agent from '../api/agent'
import { Auction } from '../models/Auction'
import { store } from './store'
import { AuctionFilter, FilterModes, FilterModesDate } from '../models/modes'
import { Post } from '../models/Post'
import { FavoriteFilter } from '../models/Filter'

type AuctionAndPost = Auction | Post

export default class FavoriteAuctionsStore {
  originalFavorites: AuctionAndPost[] = []

  favoriteRegistry = new Map<string, AuctionAndPost>()

  loadingFavorites = false

  favoriteFilter = {
    category: FilterModes.all,
    sorting: FilterModesDate.all,
    type: AuctionFilter.none,
    title: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  get favorites() {
    let allFavorites = Array.from(this.favoriteRegistry.values())
    // Sorting by Object Type ex. Auction or Post
    if (this.favoriteFilter.category === FilterModes.auction) {
      allFavorites = this.favoriteAuctions.filter((auction) => {
        if (this.isAuction(auction)) {
          const startingDate = new Date(auction.startingDate).getTime()
          const deadline = new Date(auction.deadline).getTime()

          if (this.favoriteFilter.type === AuctionFilter.upcoming) {
            return startingDate >= Date.now()
          }
          if (this.favoriteFilter.type === AuctionFilter.active) {
            return startingDate <= Date.now() && deadline >= Date.now()
          }
          if (this.favoriteFilter.type === AuctionFilter.finished) {
            return deadline < Date.now()
          }
          return true
        }
      })
    } else if (this.favoriteFilter.category === FilterModes.post) {
      allFavorites = this.favoritePosts
    }
    if (this.favoriteFilter.sorting === FilterModesDate.newest) {
      allFavorites = allFavorites.sort((a, b) => {
        const createdAtA = new Date(a.createdAt as string)?.getTime() ?? 0
        const createdAtB = new Date(b.createdAt as string)?.getTime() ?? 0

        return createdAtB - createdAtA
      })
    } else if (this.favoriteFilter.sorting === FilterModesDate.oldest) {
      allFavorites = allFavorites.sort((a, b) => {
        const createdAtA = new Date(a.createdAt as string)?.getTime() ?? 0
        const createdAtB = new Date(b.createdAt as string)?.getTime() ?? 0

        return createdAtA - createdAtB
      })
    }

    // Sorting by Title
    if (this.favoriteFilter.title) {
      allFavorites = allFavorites.filter((data) => {
        if (this.isAuction(data)) {
          return data.post?.title.toLowerCase().includes(this.favoriteFilter.title.toLowerCase())
        } else if (this.isPost(data)) {
          return data.title.toLowerCase().includes(this.favoriteFilter.title.toLowerCase())
        }
      })
    }

    return allFavorites
  }

  get favoriteAuctions() {
    return Array.from(this.favoriteRegistry.values()).filter((favorite) => this.isAuction(favorite))
  }

  get favoritePosts() {
    return Array.from(this.favoriteRegistry.values()).filter((favorite) => this.isPost(favorite))
  }

  setFavoriteFilter = (state: FavoriteFilter) => {
    this.favoriteFilter = state
  }

  formatDateLocal = (date: Date) => store.methodsStore.formatDateLocal(date)

  findFavorite = (id: string) => this.favoriteRegistry.has(id)

  addFavorite = async (id: string, type: FilterModes) => {
    const { status, data } = await agent.FavAuctions.addFav(id, type)

    if (status === 200) {
      runInAction(() => {
        this.favoriteRegistry.set(data.id, data)
        if (this.isAuction(data)) {
          toast.success(`"${data.post?.title}" has been added as favorite`)
        } else {
          toast.success(`"${data?.title}" has been added as favorite`)
        }
      })
    }
  }

  removeFavorite = async (id: string, title: string, type: FilterModes) => {
    const { status } = await agent.FavAuctions.remove(id, type === FilterModes.auction)

    if (status === 200) {
      runInAction(() => {
        this.favoriteRegistry.delete(id)
        toast.error(`"${title}" has been removed from favorites`)
      })
    }
  }

  isAuction(obj: any): obj is Auction {
    return typeof obj === 'object' && obj !== null && 'roundNumber' in obj
  }

  isPost(obj: any): obj is Post {
    return typeof obj === 'object' && obj !== null && 'rooms' in obj
  }

  loadFavorite = async () => {
    this.loadingFavorites = true
    const { data: resultAuctions } = await agent.FavAuctions.list()
    const { data: resultPosts } = await agent.FavAuctions.listPosts()
    this.originalFavorites = resultAuctions

    runInAction(() => {
      resultAuctions.forEach((auction) => {
        auction.startingDate = new Date(this.formatDateLocal(auction.startingDate))
        auction.deadline = new Date(this.formatDateLocal(auction.deadline))
        this.favoriteRegistry.set(auction.id, auction)
      })
      resultPosts.forEach((post) => {
        this.favoriteRegistry.set(post.id, post)
      })
      this.loadingFavorites = false
    })
  }
}
